import React, { useEffect, useState } from 'react';
import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';
import { useDispatch, useSelector } from 'react-redux';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import {
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  Row,
  Col,
  message,
  Switch,
  Checkbox,
  Alert,
} from 'antd';

import {
  UpdateDraftAccommodationProduct,
  loadDraftAccommodationProduct,
  setFormErrors,
  resetAccommodationModal,
  listAccommodationProductsData,
  getAccommodationProductById as selectAccommodationProduct
} from '../actions';

import {
  getAccommodationById,
  getAccommodationTypes,
  getDraftAccommodationProduct
} from '../selectors';

import { AccommodationProduct, AccommodationTypeValue, BookingSystem } from 'app/shared/model';
import * as api from 'app/shared/api';
import { PRODUCT_ACTIVE_PACKAGE_TOOLTIP } from 'app/shared/api/productDtoHelpers';
import { useActivePackageDeactivationLock } from '../hooks/useActivePackageDeactivationLock';

interface OwnProps {
  onClose?: () => void;
}

type AccommodationFormType = AccommodationProduct & { active?: boolean };

const ACCOMMODATION_PRODUCT_TYPE_ID = 2;

const atLeastOneCommittedTypeRule = (
  getFieldValue: (name: string) => unknown,
  otherField: 'isCommitted' | 'isNonCommitted'
) => ({
  validator(_: unknown, value: boolean) {
    if (value || getFieldValue(otherField)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Please select at least one accommodation commitment type.'));
  },
});

const AddEditAccommodationForm: React.FC<OwnProps> = ({ onClose }) => {
  const dispatch = useDispatch<any>();
  const accommodationProduct = useSelector(getAccommodationById)!;
  const draftProduct = useSelector(getDraftAccommodationProduct);
  const types = useSelector(getAccommodationTypes);
  const [form] = Form.useForm<AccommodationFormType>();
  const [isLoading, setIsLoading] = useState(false);
  const [bookingSystems, setBookingSystems] = useState<BookingSystem[]>([]);
  const [initialExcludedSystems, setInitialExcludedSystems] = useState<string[]>([]);
  const [isExclusionLoading, setIsExclusionLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    api.getAllBookingSystems()
      .then(systems => {
        if (isMounted) {
          setBookingSystems(systems);
        }
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load booking systems');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Populate form when selection changes
  useEffect(() => {
    const product = accommodationProduct;
    if (product) {
      const active = typeof product.active === 'boolean' ? product.active : !product.decommissionDate;
      form.setFieldsValue({
        ...product,
        typeId: !product.id || product.typeId === 0 ? undefined : product.typeId,
        active,
      });
    } else {
      form.resetFields();
      setInitialExcludedSystems([]);
    }
  }, [accommodationProduct?.id, form]);

  useEffect(() => {
    const product = accommodationProduct;
    if (!product) {
      form.setFieldsValue({ excludedBookingSystemIds: [] });
      setInitialExcludedSystems([]);
      setIsExclusionLoading(false);
      return;
    }

    if (!product.id) {
      setInitialExcludedSystems([]);
      form.setFieldsValue({ excludedBookingSystemIds: [] });
      setIsExclusionLoading(false);
      return;
    }

    let isCancelled = false;
    setIsExclusionLoading(true);
    api.getBookingSystemProductExclusions()
      .then(exclusions => {
        if (isCancelled) {
          return;
        }
        const selected = exclusions
          .filter(summary =>
            Array.isArray(summary.productsExcluded) &&
            summary.productsExcluded.some(
              productExclusion =>
                Number(productExclusion.productId) === Number(product.id) &&
                Number(productExclusion.productTypeId) === ACCOMMODATION_PRODUCT_TYPE_ID
            )
          )
          .map(summary => String(summary.bookingSystemId));
        setInitialExcludedSystems(selected);
      })
      .catch((err: any) => {
        if (!isCancelled) {
          setInitialExcludedSystems([]);
          message.error(err?.message ?? 'Failed to load booking system exclusions');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsExclusionLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [accommodationProduct, form]);

  useEffect(() => {
    form.setFieldsValue({ excludedBookingSystemIds: initialExcludedSystems });
  }, [initialExcludedSystems, form]);

  const divisionId =
    getSelectedDivisionId() ||
    Number(accommodationProduct?.division) ||
    0;
  const formDisabled = Boolean(accommodationProduct?.entityUpdate);
  const { activeDeactivationLocked } = useActivePackageDeactivationLock(
    accommodationProduct,
    ACCOMMODATION_PRODUCT_TYPE_ID,
    divisionId,
    accommodationProduct
  );

  // Keep draft in sync on any change
  const onValuesChange = (_: any, all: AccommodationFormType) => {
    const base = draftProduct ?? accommodationProduct;
    let decommissionDate = base?.decommissionDate;
    if ('active' in all) {
      decommissionDate = all.active ? undefined : new Date();
    }
    const { active, ...cleanAll } = all;
    const excludedBookingSystemIds = Array.isArray(all.excludedBookingSystemIds)
      ? all.excludedBookingSystemIds.map(id => String(id))
      : base?.excludedBookingSystemIds ?? [];
    dispatch(UpdateDraftAccommodationProduct({
      ...base,
      ...cleanAll,
      excludedBookingSystemIds,
      decommissionDate,
      active: all.active !== false
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const values = await form.validateFields();
      const base = draftProduct ?? accommodationProduct;
      if (!base) {
        message.error("Nothing to save");
        return;
      }
      const active = activeDeactivationLocked ? true : values.active !== false;
      const draftToSave = {
        ...base,
        ...values,
        active,
        maxAge: values.maxAge ?? null,
      };
      const { ok, message: msg, productId: savedProductId } = await api.saveAccommodationProduct(draftToSave);
      if (!ok) {
        message.error(msg || 'Save failed');
        return;
      }
      const resolvedProductId = savedProductId ?? draftToSave?.id ?? 0;
      const selectedSystemValues = (form.getFieldValue('excludedBookingSystemIds') ?? []) as (string | number)[];
      const selectedSystems = selectedSystemValues.map(value => String(value));

      if (Number(resolvedProductId) > 0) {
        const additions = selectedSystems.filter(id => !initialExcludedSystems.includes(id));
        const removals = initialExcludedSystems.filter(id => !selectedSystems.includes(id));

        for (const systemId of additions) {
          const numericId = Number(systemId);
          const normalizedSystemId = Number.isNaN(numericId) ? systemId : numericId;
          const result = await api.addBookingSystemProductExclusion(
            normalizedSystemId,
            ACCOMMODATION_PRODUCT_TYPE_ID,
            resolvedProductId
          );
          if (!result.ok) {
            throw new Error(result.message ?? `Failed to exclude this product from booking system ${systemId}.`);
          }
        }

        for (const systemId of removals) {
          const numericId = Number(systemId);
          const normalizedSystemId = Number.isNaN(numericId) ? systemId : numericId;
          const result = await api.removeBookingSystemProductExclusion(
            normalizedSystemId,
            ACCOMMODATION_PRODUCT_TYPE_ID,
            resolvedProductId
          );
          if (!result.ok) {
            throw new Error(result.message ?? `Failed to include this product for booking system ${systemId}.`);
          }
        }

        setInitialExcludedSystems(selectedSystems);
      } else if (selectedSystems.length > 0) {
        message.warning('Saved, but booking system exclusions could not be updated because the product id was unavailable.');
      }
      message.success(Number(draftToSave?.id ?? 0) > 0 ? "Updated!" : "Created!");

     try {
      const safeList = await api.getAccommodationProducts();
      const itemIndex = safeList.findIndex(a => a.id === savedProductId);

        if(savedProductId && itemIndex === -1)
        {
          draftToSave.id = savedProductId || 0;
          draftToSave.active = true;
          draftToSave.entityUpdate = true;

          safeList.push(draftToSave);

          dispatch(listAccommodationProductsData(safeList));
        }
        else
        {
            safeList[itemIndex] = { ...safeList[itemIndex], entityUpdate: true };
            dispatch(listAccommodationProductsData(safeList));
        }
      } catch (e: any) {
        message.warning('Saved, but failed to refresh the list. Please refresh the page.');
      }
      dispatch(setFormErrors([]));
      dispatch(loadDraftAccommodationProduct(undefined!));
      dispatch(selectAccommodationProduct(undefined as any));
      dispatch(resetAccommodationModal());
      onClose?.();
    } catch (err: any) {
      message.error(`Save failed: ${err?.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!accommodationProduct) {
    return <div></div>;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={formDisabled}
      onValuesChange={onValuesChange}
      onFinish={handleSubmit}
      initialValues={{
        ...accommodationProduct,
        typeId:
          !accommodationProduct.id || accommodationProduct.typeId === 0
            ? undefined
            : accommodationProduct.typeId,
        excludedBookingSystemIds: accommodationProduct.excludedBookingSystemIds ?? [],
        active: typeof accommodationProduct.active === 'boolean'
          ? accommodationProduct.active
          : !accommodationProduct.decommissionDate,
      }}
    >
      {formDisabled && (
        <Alert type="info" title="Publishing an update" description="This record is locked while an update is being published. Editing is disabled." showIcon style={{ marginBottom: 16 }} />
      )}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter the accommodation name' }]}
          >
            <Input maxLength={100} placeholder='Enter Accommodation name'/>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="typeId"
            label="Accommodation Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              placeholder="Select Type"
              options={types
                .filter(i => i.id > 0)
                .map(i => ({ value: i.id, label: i.name }))}
              showSearch={{ optionFilterProp: 'label' }}
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="minAge"
            label="Min Age"
            getValueProps={(value) => ({ value: value === 0 ? undefined : value })}
            rules={[
              { required: true, message: 'Please enter minimum age' },
              { type: 'number', min: 1, message: 'Must be at least 1' }
            ]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter minimum age" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
              name="maxAge"
              label="Max Age"
              getValueProps={(value) => ({ value: value === 0 ? undefined : value })}
              dependencies={['minAge']}
              rules={[
                { type: 'number', min: 1, message: 'Must be at least 1' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value === undefined || value === null || value === '') return Promise.resolve();

                    const minAge = getFieldValue('minAge');
                    if (minAge != null && Number(value) < Number(minAge)) {
                      return Promise.reject(new Error(`Max Age must be ≥ Min Age (${minAge})`));
                    }
                    return Promise.resolve();
                  }
                })
              ]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter maximum age" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
              name="minStay"
              label="Min Stay (Weeks)"
              getValueProps={(value) => ({ value: value === 0 ? undefined : value })}
              rules={[
                { required: true, message: 'Please enter minimum stay' },
                { type: 'number', min: 1, message: 'Must be at least 1 week' }
              ]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter minimum stay" />
          </Form.Item>
        </Col>
        <Col span={12} />
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="isCommitted"
            valuePropName="checked"
            dependencies={['isNonCommitted']}
            rules={[({ getFieldValue }) => atLeastOneCommittedTypeRule(getFieldValue, 'isNonCommitted')]}
          >
              <Switch checkedChildren="Committed" unCheckedChildren="Committed" />
          </Form.Item>
        </Col>
    
        <Col span={12}>
          <Form.Item
            name="isNonCommitted"
            valuePropName="checked"
            dependencies={['isCommitted']}
            rules={[({ getFieldValue }) => atLeastOneCommittedTypeRule(getFieldValue, 'isCommitted')]}
          >
              <Switch checkedChildren="Non Committed" unCheckedChildren="Non Committed" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <ActiveStatusFormItem
            name="active"
            tooltip={activeDeactivationLocked ? PRODUCT_ACTIVE_PACKAGE_TOOLTIP : undefined}
            disabled={activeDeactivationLocked}
          />
        </Col>
        <Col span={12}/>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item
            name="excludedBookingSystemIds"
            label={
              <>
                <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
                {' this accommodation product from the following systems:'}
              </>
            }
          >
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              disabled={isExclusionLoading}
            >
              {bookingSystems.map(system => (
                <Checkbox key={system.id} value={String(system.id)}>
                  {system.name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Col>
      </Row>
      <Row justify="end" gutter={4}>
        <Form.Item style={{ marginTop: 18 }} >
          <Button type="primary" htmlType="submit" loading={isLoading} disabled={formDisabled} block>
            Save
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
};

export default AddEditAccommodationForm;